import { BACKOFFICE_ORGANIZATIONS_MONITORING } from './backoffice-organizations-monitoring'

export const BACKOFFICE_ORGANIZATIONS = {
  MANAGE: {
    TITLE: 'Gestionar organización',
    TABS: {
      LINKED_DOCUMENTS: 'Documentos vinculados',
      EMPLOYEE_CENTER: 'Employee Center',
      MONITORING: 'Monitorización',
      TRUST_CENTER: 'Centro de Confianza',
    },
    EMPLOYEE_CENTER: {
      DESCRIPTION: 'Elige qué documentos muestra esta organización a sus empleados en el Employee Center.',
      VISIBLE_TOOLTIP:
        'Activar un documento lo hace elegible. Llega a los empleados cuando está traducido al idioma ' +
        'de la organización y tiene una versión aprobada, o un enlace de documento utilizable en ' +
        'organizaciones que siguen en Drive.',
      ALL_DEPARTMENTS: 'Todos los departamentos',
      NO_APPROVED_VERSION: 'Activado, pero aún no visible: sin versión aprobada',
      MISSING_TRANSLATION: 'Activado, pero aún no visible: sin traducción en el idioma de la organización',
      ENABLED_COUNT: '{count} visibles para empleados',
      HIDDEN_BY_FILTER: '{count} activados fuera de este filtro',
      NEVER_REVIEWED: '{count} sin revisar',
      NEVER_REVIEWED_OTHER_TYPES: 'Hay documentos sin revisar en {count} tipos más',
      SHOW_ALL_TYPES: 'Ver todos los tipos',
      ENABLE_ALL_IN_VIEW: 'Activar todo lo mostrado',
      ENABLING: 'Activando...',
      ALL_TYPES: 'Todos los tipos',
      SEARCH_PLACEHOLDER: 'Buscar por código o nombre...',
      LOADING: 'Cargando...',
      REFRESH: 'Actualizar',
      EMPTY: 'Ningún documento coincide.',
      TABLE: {
        CODE: 'Código',
        NAME: 'Nombre',
        TYPE: 'Tipo',
        STATUS: 'Estado',
        VISIBLE: 'Employee Center',
        DEPARTMENTS: 'Departamentos',
      },
      SNACKBAR: {
        SAVED: 'Cambios guardados',
      },
    },
    LINKED_DOCUMENTS: {
      PRIMARY_ACTION: 'Vincular documentos de Google Drive',
      LINKING: 'Vinculando...',
      DESCRIPTION: 'Vincula URLs compartidas de Google Drive a los documentos de cumplimiento de esta organización.',
      MIGRATION_BLOCKED:
        'Esta organización tiene documentos migrados a la plataforma. La vinculación de documentos de Google Drive está deshabilitada para evitar sobrescribirlos. Los registros son hojas de cálculo y no se pueden migrar, así que usa «Clonar registros desde Drive» para esos.',
      CLONE_REGISTERS: 'Clonar registros desde Drive',
      CLONING: 'Clonando...',
      CLONE_REGISTERS_HINT:
        'Copia las hojas de cálculo de registros desde la carpeta de origen de Drive y las vincula. Se puede volver a ejecutar sin riesgo y nunca afecta a documentos que tienen una versión en la plataforma.',
      PROGRESS: {
        TITLE: 'Vinculación en curso',
        STARTING: 'Iniciando…',
        LINKING: 'Copiando y mapeando documentos…',
        REFRESHING: 'Actualizando documentos vinculados…',
      },
      CLONE_PROGRESS: {
        TITLE: 'Clonando registros',
        STARTING: 'Iniciando…',
        CLONING: 'Copiando hojas de cálculo de registros…',
        REFRESHING: 'Actualizando documentos vinculados…',
      },
      SEARCH_PLACEHOLDER: 'Buscar por código o nombre…',
      REFRESH: 'Actualizar',
      LOADING: 'Cargando...',
      EMPTY: 'No se encontraron documentos.',
      URL_PLACEHOLDER: 'https://…',
      OPEN: 'Abrir',
      COPY: 'Copiar',
      SAVE: 'Guardar',
      SAVING: 'Guardando...',
      TABLE: {
        CODE: 'Código',
        NAME: 'Nombre',
        URL: 'URL del documento',
        ACTIONS: 'Acciones',
      },
      SNACKBAR: {
        LINKED: ({ mappedCount }: { mappedCount: number }) => `Se vincularon ${mappedCount} URLs de documentos`,
        LINKED_WITH_WARNINGS: ({
          mappedCount,
          missingCode,
          missingUrl,
        }: {
          mappedCount: number
          missingCode: number
          missingUrl: number
        }) =>
          `Se vincularon ${mappedCount} URLs de documentos (omitidos: ${missingCode} sin códigos, ${missingUrl} sin enlaces)`,
        REGISTERS_CLONED: ({ clonedCount, mappedCount }: { clonedCount: number; mappedCount: number }) =>
          `Se clonaron ${clonedCount} archivo(s) de registro y se vincularon ${mappedCount} documento(s)`,
        REGISTERS_CLONED_WITH_WARNINGS: ({
          clonedCount,
          mappedCount,
          missingCode,
          missingUrl,
        }: {
          clonedCount: number
          mappedCount: number
          missingCode: number
          missingUrl: number
        }) =>
          `Se clonaron ${clonedCount} archivo(s) de registro y se vincularon ${mappedCount} documento(s) (omitidos: ${missingCode} sin código de documento, ${missingUrl} sin enlace)`,
        REGISTERS_UP_TO_DATE: ({ mappedCount }: { mappedCount: number }) =>
          `No hay archivos de registro nuevos para clonar; ${mappedCount} documento(s) ya vinculados`,
        SAVED: 'URL guardada',
        COPIED: 'Copiado al portapapeles',
        COPY_FAILED: 'Error al copiar',
      },
    },
    MONITORING: BACKOFFICE_ORGANIZATIONS_MONITORING,
    TRUST_CENTER: {
      TAB_LABEL: 'Centro de Confianza',
      BANNER: {
        TITLE: 'Aviso de publicación',
        DESCRIPTION: 'Guardar cualquier sección hace que el Centro de Confianza sea accesible públicamente.',
        URL_LABEL: 'URL pública',
      },
      ORG_DATA: {
        TITLE: 'Datos de la organización',
        TAGLINE_LABEL: 'Eslogan',
        TAGLINE_PLACEHOLDER: 'La misión de tu empresa o declaración de seguridad',
        SHOW_CONTROLS_LABEL: 'Mostrar sección de Controles',
        SHOW_CONTROLS_DESCRIPTION:
          'Cuando está habilitado, la pestaña Controles y el resumen son visibles para los visitantes del Centro de Confianza público.',
        VULNERABILITY_EMAIL_LABEL: 'Email para reportar vulnerabilidades',
        QUESTIONNAIRES_EMAIL_LABEL: 'Email para cuestionarios de seguridad',
        CONTACT_EMAILS_DESCRIPTION:
          'Se muestran en las tarjetas de contacto del Centro de Confianza público. Déjalos vacíos para usar el buzón de Qalea.',
        SNACKBAR_SAVED: 'Datos del Centro de Confianza guardados',
        SNACKBAR_ERROR: 'Error al guardar los datos del Centro de Confianza',
      },
      STANDARDS: {
        TITLE: 'Estándares',
        LABEL: 'Estándares mostrados en el Centro de Confianza',
        PLACEHOLDER: 'Seleccionar estándares...',
        SEARCH_PLACEHOLDER: 'Buscar estándares...',
        EMPTY_TEXT: 'No se encontraron estándares.',
        SNACKBAR_SAVED: 'Estándares guardados',
        SNACKBAR_ERROR: 'Error al guardar los estándares',
      },
      SUBPROCESSORS: {
        TITLE: 'Subencargados',
        ADD: 'Añadir subencargado',
        EMPTY: 'No hay subencargados todavía.',
        NAME_LABEL: 'Nombre',
        LOGO_URL_LABEL: 'URL del logo',
        PURPOSE_LABEL: 'Propósito',
        LOCATION_LABEL: 'Ubicación',
        SAVE: 'Guardar',
        CANCEL: 'Cancelar',
        EDIT: 'Editar',
        DELETE: 'Eliminar',
        TABLE: {
          NAME: 'Nombre',
          PURPOSE: 'Propósito',
          LOCATION: 'Ubicación',
          ACTIONS: 'Acciones',
        },
        SNACKBAR_ADDED: 'Subencargado añadido',
        SNACKBAR_UPDATED: 'Subencargado actualizado',
        SNACKBAR_DELETED: 'Subencargado eliminado',
        SNACKBAR_ERROR: 'Error al actualizar los subencargados',
      },
      RESOURCES: {
        TITLE: 'Recursos',
        ADD: 'Añadir recurso',
        EMPTY: 'No hay recursos todavía.',
        NAME_LABEL: 'Nombre',
        DESCRIPTION_LABEL: 'Descripción',
        URL_LABEL: 'URL (opcional)',
        TYPE_LABEL: 'Tipo',
        TYPE_PUBLIC: 'Público',
        TYPE_PRIVATE: 'Privado',
        SAVE: 'Guardar',
        CANCEL: 'Cancelar',
        EDIT: 'Editar',
        DELETE: 'Eliminar',
        TABLE: {
          NAME: 'Nombre',
          DESCRIPTION: 'Descripción',
          TYPE: 'Tipo',
          ACTIONS: 'Acciones',
        },
        SNACKBAR_ADDED: 'Recurso añadido',
        SNACKBAR_UPDATED: 'Recurso actualizado',
        SNACKBAR_DELETED: 'Recurso eliminado',
        SNACKBAR_ERROR: 'Error al actualizar los recursos',
      },
    },
    SAVE: {
      LABEL: 'Guardar cambios',
      SAVING: 'Guardando...',
    },
  },
}
